import { TableCell, TableHeader, TanStackTableRoot } from '@design';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { TeamsGuard } from '@terminal/config/TeamsGuard';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useThemeChildren } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { queryKeys } from '@terminal/routes/query-params';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import clsx from 'clsx';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { COUNT_COLUMN_ID, getColumns } from './getColumnUtils';
import { LoaderCell } from './loader-cell/LoaderCell';
import styles from './ParentCategoryTable.module.scss';

export interface ParentCategoryTableProps {
  entityTag?: string;
  isExposureOnly: boolean;
  parentThemeId: string;
  childThemeId: string;
  source: PosthogTrackingSource;
}

export const ParentCategoryTable = ({
  entityTag,
  isExposureOnly,
  parentThemeId,
  childThemeId,
  source,
}: ParentCategoryTableProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(['widget']);
  const { navigateToTheme } = useNavigateToThemeOverview();

  const {
    data = [],
    isFetching,
    error,
  } = useThemeChildren(parentThemeId, entityTag);

  const tableData = useMemo(
    () =>
      data.filter(item => (isExposureOnly ? item.is_company_exposed : true)),
    [data, isExposureOnly]
  );

  const columns = useMemo(
    () => getColumns({ t, total: tableData.length }),
    [tableData.length]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (error) {
    throw error;
  }

  return (
    <TeamsGuard>
      <div className={styles.tableWrapper} ref={containerRef}>
        <TanStackTableRoot data-testid="parent-category-table">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <TableHeader
                      key={header.id}
                      className={styles.styledTableHeader}
                      data-table-header
                      isHoverEnabled={false}
                      alignment={
                        header.column.id === COUNT_COLUMN_ID ? 'end' : 'start'
                      }
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </TableHeader>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isFetching ? (
              <LoaderCell
                columnLength={table.getHeaderGroups()[0].headers.length}
              />
            ) : (
              table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  className={clsx(styles.styledTableRow, {
                    [styles.rowActive]: row.original.theme.id === childThemeId,
                  })}
                  onClick={() => {
                    const searchParams = new URLSearchParams();
                    searchParams.set(queryKeys.activeSubTab, 'overview');
                    navigateToTheme({
                      themeId: row.original.theme.id,
                      source,
                      searchParams,
                    });
                  }}
                >
                  {row.getVisibleCells().map(cell => {
                    return (
                      <TableCell
                        key={cell.id}
                        className={clsx(styles.styledTableCell, {
                          [styles.cellActive]:
                            row.original.theme.id === childThemeId,
                        })}
                        data-category-cell
                        alignment={
                          cell.column.id === COUNT_COLUMN_ID ? 'right' : 'left'
                        }
                        isNumber={cell.column.id === COUNT_COLUMN_ID}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </TanStackTableRoot>
      </div>
    </TeamsGuard>
  );
};
